import { useEffect, useState } from 'react';

// Rascunhos guardados no aparelho (IndexedDB), um banco por conta: rascunhos de uma conta
// nunca aparecem para outra no mesmo navegador (seção 8.2). Dados compartilhados continuam no servidor.

export interface DraftData {
  projectId: string | null;
  projectLabel: string | null;
  quickNote: string;
  serviceDate: string | null;
  durationMinutes: number | null;
  modality: 'remoto' | 'presencial' | null;
}

export type SyncState = 'pendente' | 'sincronizado' | 'conflito' | 'erro';

export interface LocalDraft {
  clientDraftId: string;
  data: DraftData;
  baseVersion: number | null; // versão do servidor em que esta cópia se baseia
  serverId: string | null;
  syncState: SyncState;
  message: string | null;
  serverCopy: { quickNote: string | null; projectName: string | null; version: number } | null;
  updatedAt: string;
}

const dbName = (userId: string) => `technove-${userId}`;

function openDb(userId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(userId), 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('drafts')) db.createObjectStore('drafts', { keyPath: 'clientDraftId' });
      if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(userId: string, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb(userId);
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => {
      db.close();
      resolve(req.result);
    };
    t.onerror = () => {
      db.close();
      reject(t.error);
    };
  });
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const listDrafts = (userId: string) => tx<LocalDraft[]>(userId, 'drafts', 'readonly', (s) => s.getAll() as IDBRequest<LocalDraft[]>);
export const getDraft = (userId: string, id: string) => tx<LocalDraft | undefined>(userId, 'drafts', 'readonly', (s) => s.get(id) as IDBRequest<LocalDraft | undefined>);

export async function saveDraft(userId: string, draft: LocalDraft) {
  await tx(userId, 'drafts', 'readwrite', (s) => s.put(draft));
  notify();
}

export async function removeDraft(userId: string, id: string) {
  await tx(userId, 'drafts', 'readwrite', (s) => s.delete(id));
  notify();
}

// Cache mínimo para uso sem conexão (ex.: lista de projetos do seletor). Não é a base inteira.
export const cacheSet = (userId: string, key: string, value: unknown) => tx(userId, 'cache', 'readwrite', (s) => s.put({ value, at: new Date().toISOString() }, key));
export const cacheGet = async <T>(userId: string, key: string): Promise<{ value: T; at: string } | undefined> =>
  tx(userId, 'cache', 'readonly', (s) => s.get(key) as IDBRequest<{ value: T; at: string } | undefined>);

function payload(d: LocalDraft) {
  return {
    baseVersion: d.baseVersion,
    projectId: d.data.projectId,
    quickNote: d.data.quickNote,
    serviceDate: d.data.serviceDate,
    durationMinutes: d.data.durationMinutes,
    modality: d.data.modality,
  };
}

let syncing = false;

// Envia ao servidor os rascunhos pendentes. Idempotente: o servidor usa o ID criado no aparelho.
export async function syncDrafts(userId: string): Promise<void> {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  try {
    const drafts = await listDrafts(userId);
    for (const d of drafts.filter((x) => x.syncState === 'pendente' || x.syncState === 'erro')) {
      try {
        const res = await fetch(`/api/v1/executions/sync/${d.clientDraftId}`, {
          method: 'PUT',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload(d)),
        });
        const body = await res.json().catch(() => null);
        if (res.ok) {
          await saveDraft(userId, { ...d, syncState: 'sincronizado', baseVersion: body.version, serverId: body.id, message: null, serverCopy: null });
        } else if (res.status === 409 && body?.error?.code === 'version_conflict') {
          const s = body.error.details?.server;
          await saveDraft(userId, {
            ...d,
            syncState: 'conflito',
            message: body.error.message,
            serverCopy: s ? { quickNote: s.quickNote, projectName: s.projectName, version: s.version } : null,
          });
        } else if (res.status === 409 && body?.error?.code === 'no_longer_draft') {
          await saveDraft(userId, { ...d, syncState: 'conflito', message: body.error.message, serverCopy: null });
        } else if (res.status === 401 || res.status === 502 || res.status === 503 || res.status === 504) {
          // Sessão expirada ou servidor inalcançável: continua pendente e tenta de novo depois.
          if (d.syncState === 'erro') await saveDraft(userId, { ...d, syncState: 'pendente', message: null });
          return;
        } else {
          await saveDraft(userId, { ...d, syncState: 'erro', message: body?.error?.message ?? `Erro ${res.status}` });
        }
      } catch {
        return; // sem conexão: continua pendente
      }
    }
  } finally {
    syncing = false;
  }
}

// Resolve um conflito: manter a versão do servidor (descarta a do aparelho) ou a do aparelho (reenvia sobre a do servidor).
export async function resolveConflict(userId: string, d: LocalDraft, keep: 'servidor' | 'aparelho') {
  if (keep === 'servidor' || !d.serverCopy) {
    await removeDraft(userId, d.clientDraftId);
    return;
  }
  await saveDraft(userId, { ...d, syncState: 'pendente', baseVersion: d.serverCopy.version, serverCopy: null, message: null });
  await syncDrafts(userId);
}

export function useLocalDrafts(userId: string) {
  const [drafts, setDrafts] = useState<LocalDraft[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () =>
      listDrafts(userId)
        .then((d) => alive && setDrafts(d.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))))
        .catch(() => undefined);
    void load();
    listeners.add(load);
    return () => {
      alive = false;
      listeners.delete(load);
    };
  }, [userId]);
  return drafts;
}

// Mantém a sincronização ativa enquanto o usuário está logado.
export function useDraftSync(userId: string) {
  useEffect(() => {
    void syncDrafts(userId);
    const onOnline = () => void syncDrafts(userId);
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(() => void syncDrafts(userId), 60_000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(timer);
    };
  }, [userId]);
}

export const isUnsynced = (d: LocalDraft) => d.syncState !== 'sincronizado';
